import { useCallback, useMemo, useState } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { PublicSignInGate, PublicLoginDialog } from '@discovr/ui';
import { useAuth } from '@discovr/common';
import { auth as authApi } from '@discovr/api';
import { usePostLoginRedirect } from '@/auth/usePostLoginRedirect';
import { BRAND_APP_SURFACE } from '@/auth/appSurface';

const viewRoute = getRouteApi('/view');

/**
 * Sign-in gate for a shared brand campaign link (`/campaign/{slug}?…`) opened
 * while signed out. Instead of the full login page, the guard routes here to a
 * "Sign in to view this" card whose button opens the PublicLoginDialog modal
 * (password / Google / OTP). On success, `user` flips truthy and the effect
 * below forwards to the campaign screen the link pointed at.
 */
export default function SharedLinkGatePage() {
  const { redirect } = viewRoute.useSearch();
  const { signIn, signInWithGoogle, signInWithOtpToken } = useAuth();
  const [loginOpen, setLoginOpen] = useState(false);

  // Signed in (via the dialog, or a tab that already had a session) → forward to
  // the shared destination.
  usePostLoginRedirect(redirect);

  const requestLogin = useCallback(() => setLoginOpen(true), []);
  // Closing is the whole job — the effect above redirects once `user` updates,
  // exactly like PublicShareViewPage relies on its resolver re-render.
  const handleSignedIn = useCallback(() => setLoginOpen(false), []);
  const handleRequestOtp = useCallback(async (email: string) => {
    await authApi.requestOtp(email, BRAND_APP_SURFACE);
  }, []);
  const handleVerifyOtp = useCallback(
    async ({ email, code }: { email: string; code: string }) => {
      const { firebaseCustomToken } = await authApi.verifyOtp({ email, app: BRAND_APP_SURFACE, code });
      await signInWithOtpToken(firebaseCustomToken);
    },
    [signInWithOtpToken],
  );
  const otpProps = useMemo(
    () => ({ onRequestOtp: handleRequestOtp, onVerifyOtp: handleVerifyOtp }),
    [handleRequestOtp, handleVerifyOtp],
  );

  return (
    <>
      <PublicSignInGate
        title="Sign in to view this"
        description="This is a shared campaign link. Sign in with your Discovr account to open it — only members of the campaign's brand can view it."
        onSignIn={requestLogin}
      />
      <PublicLoginDialog
        open={loginOpen}
        onOpenChange={setLoginOpen}
        signIn={signIn}
        onSignedIn={handleSignedIn}
        onGoogleSignIn={signInWithGoogle}
        otp={otpProps}
      />
    </>
  );
}
